import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../../packages/ConfirmationPopup";
import CustomModal from "../../../packages/CustomModal";
import IconButton from "../../../packages/IconButton";
import WPAgGrid from "../../../packages/WPAgGrid/WPAgGrid";
import WPTertiaryButton from "../../../packages/WPTertiaryButton";
import WPMessageBanner, {
  BANNER_TYPES,
} from "../../../packages/WPMessageBanner/WPMessageBanner";
import { BackIcon } from "../../../packages/SVGs";

import {
  APP_ROUTES,
  CustomModalButtons,
  FEATURE_SCOPE,
} from "../../../constants/global.constants";
import {
  checkChemicalUsageData,
  deleteChemicalLibraryData,
  deleteUsagePlant,
} from "../ChemicalLibraryDialog/ChemicalLibraryDialog.api";
import {
  USAGE_DIALOG_TABLE_HEIGHT,
  USAGE_DIALOG_WIDTH,
} from "./ChemicalLibraryDeleteDialog.constants";
import {
  formatDateTime,
  formatUsageChemicalName,
  getUsageOpenUrl,
  mapUsageRows,
} from "./ChemicalLibraryDeleteDialog.util";

import "./ChemicalLibraryDeleteDialog.scss";

const OPEN_REDIRECT_PATH_KEY = "openRedirectPath";

const getCurrentPlantIdFromUrl = () =>
  new URLSearchParams(window.location.search).get("plantId");

const ChemicalLibraryDeleteDialog = ({
  chemicalData,
  usageList = [],
  scope = FEATURE_SCOPE.USER,
  onChemicalDeleted,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isUserScope = scope === FEATURE_SCOPE.USER;

  const [usageRows, setUsageRows] = useState([]);
  const [usageChemicalName, setUsageChemicalName] = useState("");
  const [showUsageDialog, setShowUsageDialog] = useState(false);
  const [selectedUsageRow, setSelectedUsageRow] = useState(null);
  const [showUsageDeleteConfirm, setShowUsageDeleteConfirm] = useState(false);
  const [usageDeleteErrorMessage, setUsageDeleteErrorMessage] = useState("");
  const [showUsageDeleteError, setShowUsageDeleteError] = useState(false);
  const [isUsageRefreshLoading, setIsUsageRefreshLoading] = useState(false);
  const [isUsageDeleteLoading, setIsUsageDeleteLoading] = useState(false);

  const resetDeleteFlow = () => {
    setUsageRows([]);
    setUsageChemicalName("");
    setShowUsageDialog(false);
    setSelectedUsageRow(null);
    setShowUsageDeleteConfirm(false);
    setShowUsageDeleteError(false);
    setUsageDeleteErrorMessage("");
    onClose?.();
  };

  const handleUsageRowDeleteClick = (row) => {
    setSelectedUsageRow(row);
    setShowUsageDeleteConfirm(true);
  };

  const openUsageTarget = (targetUrl) => {
    const target = new URL(targetUrl);
    const targetPathWithSearch = `${target.pathname}${target.search}`;
    window.sessionStorage.setItem("lastVisitedURL", targetPathWithSearch);
    window.localStorage.setItem(OPEN_REDIRECT_PATH_KEY, targetPathWithSearch);
    window.open(targetUrl, "_blank");
  };

  const handleUsageRowOpen = (row) => {
    const targetUrl = getUsageOpenUrl(row, window.location.origin);
    if (!targetUrl) {
      return;
    }

    openUsageTarget(targetUrl);
  };

  const usageColumns = useMemo(
    () => [
      {
        headerName: t("chemicalLibrary.usagePlant"),
        field: "plantName",
        width: 200,
        suppressSizeToFit: true,
        tooltip: true,
      },
      {
        headerName: t("chemicalLibrary.lastModified"),
        field: "lastModified",
        width: 210,
        valueFormatter: (params) => formatDateTime(params.value),
      },
      {
        headerName: t("chemicalLibrary.createdDate"),
        field: "createdDate",
        width: 210,
        valueFormatter: (params) => formatDateTime(params.value),
      },
      {
        headerName: t("chemicalLibrary.actions"),
        field: "actions",
        maxWidth: 115,
        sortable: false,
        cellRenderer: ({ data }) => (
          <div className="chemical-usage-actions">
            <WPTertiaryButton
              label={t("chemicalLibrary.open")}
              className="chemical-usage-action-link chemical-usage-action-open"
              onClick={() => handleUsageRowOpen(data)}
            />
            <WPTertiaryButton
              label={t("ValueStrings.delete")}
              className="chemical-usage-action-link chemical-usage-action-delete"
              onClick={() => handleUsageRowDeleteClick(data)}
            />
          </div>
        ),
      },
    ],
    [t],
  );

  const closeUsageDialog = () => {
    resetDeleteFlow();
  };

  const navigatingToHome = () => {
    resetDeleteFlow();
    navigate(`/${APP_ROUTES.PLANTS}`, { replace: true });
  };

  const handleUsageDeleteConfirmClose = async (action) => {
    setShowUsageDeleteConfirm(false);

    if (action !== CustomModalButtons.CONFIRM || !selectedUsageRow) {
      setSelectedUsageRow(null);
      return;
    }

    setIsUsageDeleteLoading(true);
    try {
      const isLastUsageRow = usageRows.length <= 1;
      const isDeletingCurrentPlant =
        String(selectedUsageRow?.plantId) ===
        String(getCurrentPlantIdFromUrl());
      const result = await deleteUsagePlant(selectedUsageRow.plantId, t);

      if (!result?.success) {
        setUsageDeleteErrorMessage(
          result?.errorMessage || t("notificationStrings.somethingWrong"),
        );
        setShowUsageDeleteError(true);
        return;
      }

      const updatedRows = usageRows.filter(
        (row) => String(row?.plantId) !== String(selectedUsageRow.plantId),
      );
      setUsageRows(updatedRows);

      if (isLastUsageRow) {
        const isChemicalDeleted = await deleteChemicalLibraryData(
          chemicalData?.chem_id,
          t,
        );

        if (isChemicalDeleted) {
          await onChemicalDeleted?.(chemicalData?.chem_id);
        }
      }

      if (isDeletingCurrentPlant) {
        navigatingToHome();
        return;
      }

      if (isLastUsageRow || updatedRows.length === 0) {
        resetDeleteFlow();
      }
    } finally {
      setIsUsageDeleteLoading(false);
      setSelectedUsageRow(null);
    }
  };

  const handleUsageDeleteErrorClose = () => {
    setShowUsageDeleteError(false);
    setUsageDeleteErrorMessage("");
  };

  const isLastUsageDelete = usageRows.length <= 1;

  const handleUsageRefresh = async () => {
    if (!chemicalData?.chem_id) {
      return;
    }

    setIsUsageRefreshLoading(true);
    try {
      const usageList = await checkChemicalUsageData(chemicalData.chem_id, t);
      if (!Array.isArray(usageList)) {
        return;
      }

      const mappedUsageRows = mapUsageRows(usageList, t);
      setUsageRows(mappedUsageRows);

      if (mappedUsageRows.length === 0) {
        resetDeleteFlow();
      }
    } finally {
      setIsUsageRefreshLoading(false);
    }
  };

  useEffect(() => {
    if (!chemicalData?.chem_id || !isUserScope) {
      return;
    }

    const mappedUsageRows = mapUsageRows(usageList, t);
    if (mappedUsageRows.length === 0) {
      resetDeleteFlow();
      return;
    }

    setUsageChemicalName(
      formatUsageChemicalName(
        chemicalData?.display_name ||
          chemicalData?.symbol ||
          chemicalData?.chemical ||
          "",
      ),
    );
    setUsageRows(mappedUsageRows);
    setShowUsageDialog(true);
  }, [chemicalData?.chem_id, isUserScope, usageList]);

  return (
    <>
      {showUsageDialog && (
        <CustomModal
          header={
            <div className="chemical-usage-header">
              <IconButton
                tooltip={t("ValueStrings.back")}
                onClick={closeUsageDialog}
              >
                <BackIcon />
              </IconButton>
              <span className="chemical-usage-header-title">
                {t("chemicalLibrary.reviewBeforeDeletingChemical")}
              </span>
            </div>
          }
          onModalClose={closeUsageDialog}
          isLoading={isUsageRefreshLoading || isUsageDeleteLoading}
          loadingText={
            isUsageDeleteLoading
              ? isLastUsageDelete
                ? t("chemicalLibrary.deletingPlantAndChemical")
                : t("chemicalLibrary.deletingPlants")
              : t("chemicalLibrary.refreshingList")
          }
          cancelBtn={t("ValueStrings.cancel")}
          modalFooter={
            <WPTertiaryButton
              label={t("chemicalLibrary.refreshList")}
              onClick={handleUsageRefresh}
              variant="text"
              size="small"
              className="chemical-usage-action-link"
            />
          }
          customclass="chemical-lib-usage-dialog"
          width={USAGE_DIALOG_WIDTH}
        >
          <div className="chemical-usage-container">
            <WPMessageBanner
              type={BANNER_TYPES.error}
              className="chemical-error-banner"
              header={t("chemicalLibrary.chemicalAlreadyUsed", {
                chemical: usageChemicalName || t("chemicalLibrary.chemical"),
              })}
              message={t("chemicalLibrary.deletePlantWarningMsg")}
            />

            <WPAgGrid
              rowData={usageRows}
              columnDefs={usageColumns}
              tableHeight={USAGE_DIALOG_TABLE_HEIGHT}
              defaultColDef={{ sortable: false }}
              rowSelection={undefined}
              gridOptions={{
                suppressRowClickSelection: true,
                suppressCellFocus: true,
              }}
            />
          </div>
        </CustomModal>
      )}

      {showUsageDeleteConfirm && (
        <ConfirmationPopup
          type={DIALOG_TYPES.ERROR}
          header={
            isLastUsageDelete
              ? t("chemicalLibrary.deleteChemicalTitle", {
                  chemical: usageChemicalName || t("chemicalLibrary.chemical"),
                })
              : t("chemicalLibrary.deletePlantTitle")
          }
          description={
            isLastUsageDelete
              ? t("chemicalLibrary.deleteChemicalDescription")
              : t("chemicalLibrary.deletePlantDescription")
          }
          confirmBtn={
            isLastUsageDelete
              ? t("chemicalLibrary.deleteChemical")
              : t("chemicalLibrary.deletePlant")
          }
          cancelBtn={t("ValueStrings.cancel")}
          onClose={handleUsageDeleteConfirmClose}
        />
      )}

      {showUsageDeleteError && (
        <ConfirmationPopup
          type={DIALOG_TYPES.ERROR}
          header={t("notificationStrings.somethingWrong")}
          description={usageDeleteErrorMessage}
          confirmBtn={t("ValueStrings.okay")}
          onClose={handleUsageDeleteErrorClose}
        />
      )}
    </>
  );
};

export default ChemicalLibraryDeleteDialog;
